import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../../components/ui/PageLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui/Toast';
import { supabase } from '../../lib/supabase';
import { User, Building, Bell, Shield, Save, Eye, EyeOff, Loader, Lock, Phone, CreditCard, CheckCircle, Mail } from 'lucide-react';
import EmailDeliveryTracker from './components/EmailDeliveryTracker';

const ProfileSettings = () => {
  const { user, userProfile, isInitialized, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('personal');
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [personalInfo, setPersonalInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobTitle: '',
  });

  const [businessInfo, setBusinessInfo] = useState({
    companyName: '',
    businessType: '',
    industry: '',
    website: '',
  });

  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: '',
  });

  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    smsNotifications: false,
    marketingEmails: true,
    securityAlerts: true,
    weeklyReports: true,
    leadAlerts: true,
  });

  const [fieldErrors, setFieldErrors] = useState({});

  // Auth guard
  useEffect(() => {
    if (!isInitialized || authLoading) return;
    if (!user) {
      navigate('/login', { replace: true });
    }
  }, [user, isInitialized, authLoading, navigate]);

  // Populate form when user/profile loads
  useEffect(() => {
    if (user) {
      setPersonalInfo({
        firstName: userProfile?.first_name || user?.user_metadata?.first_name || '',
        lastName: userProfile?.last_name || user?.user_metadata?.last_name || '',
        email: user?.email || '',
        phone: userProfile?.phone || user?.user_metadata?.phone || '',
        jobTitle: userProfile?.job_title || user?.user_metadata?.job_title || '',
      });
      setBusinessInfo({
        companyName: userProfile?.company_name || user?.user_metadata?.company_name || '',
        businessType: userProfile?.business_type || '',
        industry: userProfile?.industry || '',
        website: userProfile?.website || '',
      });
      if (userProfile?.notification_preferences) {
        setNotifications(prev => ({ ...prev, ...userProfile?.notification_preferences }));
      }
    }
  }, [user, userProfile]);

  const tabs = [
    { id: 'personal', label: 'Personal Info', icon: User },
    { id: 'business', label: 'Business Info', icon: Building },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'payment', label: 'Payment Methods', icon: CreditCard },
    { id: 'email-tracking', label: 'Email Delivery', icon: Mail },
  ];

  const handleSavePersonal = async () => {
    if (!user?.id || !supabase) return;
    const errors = {};
    if (!personalInfo?.firstName?.trim()) errors.firstName = 'First name is required';
    if (personalInfo?.phone && !/^[\+\d\s\-\(\)]{7,}$/?.test(personalInfo?.phone)) errors.phone = 'Invalid phone number';
    if (Object.keys(errors)?.length > 0) {
      setFieldErrors(errors);
      toast?.warning('Please fix the form errors.', 'Validation Error');
      return;
    }
    setFieldErrors({});
    setSaving(true);
    try {
      const { error } = await supabase
        ?.from('user_profiles')
        ?.upsert({
          user_id: user?.id,
          first_name: personalInfo?.firstName?.trim() || null,
          last_name: personalInfo?.lastName?.trim() || null,
          full_name: `${personalInfo?.firstName?.trim() || ''} ${personalInfo?.lastName?.trim() || ''}`?.trim() || null,
          phone: personalInfo?.phone?.trim() || null,
          job_title: personalInfo?.jobTitle?.trim() || null,
          updated_at: new Date()?.toISOString()
        }, { onConflict: 'user_id' });

      if (error) throw error;
      toast?.success('Personal information updated successfully!', 'Saved ✓');
    } catch (err) {
      console.error('Error saving personal info:', err);
      toast?.error(err?.message || 'Failed to save personal information.', 'Error');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBusiness = async () => {
    if (!user?.id || !supabase) return;
    const errors = {};
    if (businessInfo?.website && !/^https?:\/\//i?.test(businessInfo?.website)) {
      errors.website = 'Website must start with http:// or https://';
    }
    if (Object.keys(errors)?.length > 0) {
      setFieldErrors(errors);
      toast?.warning('Please fix the form errors.', 'Validation Error');
      return;
    }
    setFieldErrors({});
    setSaving(true);
    try {
      const { error } = await supabase
        ?.from('user_profiles')
        ?.upsert({
          user_id: user?.id,
          company_name: businessInfo?.companyName?.trim() || null,
          business_type: businessInfo?.businessType || null,
          industry: businessInfo?.industry || null,
          website: businessInfo?.website?.trim() || null,
          updated_at: new Date()?.toISOString()
        }, { onConflict: 'user_id' });

      if (error) throw error;
      toast?.success('Business information updated successfully!', 'Saved ✓');
    } catch (err) {
      console.error('Error saving business info:', err);
      toast?.error(err?.message || 'Failed to save business information.', 'Error');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    const errors = {};
    if (!passwordData?.newPassword) errors.newPassword = 'New password is required';
    else if (passwordData?.newPassword?.length < 8) errors.newPassword = 'Password must be at least 8 characters';
    else if (!/(?=.*[a-z])(?=.*[A-Z])/?.test(passwordData?.newPassword)) errors.newPassword = 'Must contain uppercase and lowercase letters';
    if (passwordData?.newPassword !== passwordData?.confirmPassword) errors.confirmPassword = 'Passwords do not match';
    if (Object.keys(errors)?.length > 0) {
      setFieldErrors(errors);
      toast?.warning('Please fix the password errors.', 'Validation Error');
      return;
    }
    setFieldErrors({});
    setChangingPassword(true);
    try {
      const { error } = await supabase?.auth?.updateUser({ password: passwordData?.newPassword });
      if (error) throw error;
      setPasswordData({ newPassword: '', confirmPassword: '' });
      toast?.success('Password updated successfully!', 'Password Changed ✓');
    } catch (err) {
      toast?.error(err?.message || 'Failed to update password.', 'Error');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSaveNotifications = async () => {
    if (!user?.id || !supabase) return;
    setSaving(true);
    try {
      const { error } = await supabase
        ?.from('user_profiles')
        ?.upsert({
          user_id: user?.id,
          notification_preferences: notifications,
          updated_at: new Date()?.toISOString()
        }, { onConflict: 'user_id' });
      if (error) throw error;
      toast?.success('Notification preferences saved!', 'Saved ✓');
    } catch (err) {
      toast?.error(err?.message || 'Failed to save notification preferences.', 'Error');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = (field) =>
    `w-full px-4 py-3 border rounded-xl bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 text-sm transition-colors ${
      fieldErrors?.[field] ? 'border-destructive focus:ring-destructive/40' : 'border-border'
    }`;

  if (!isInitialized || authLoading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader className="w-8 h-8 animate-spin text-primary" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">Profile Settings</h1>
          <p className="text-muted-foreground mt-1">Manage your account information and preferences</p>
        </div>

        <div className="bg-card border border-border rounded-2xl shadow-elevation-1 overflow-hidden">
          {/* Tab Navigation */}
          <div className="flex overflow-x-auto border-b border-border">
            {tabs?.map(tab => {
              const TabIcon = tab?.icon;
              return (
                <button
                  key={tab?.id}
                  onClick={() => { setActiveTab(tab?.id); setFieldErrors({}); }}
                  className={`flex items-center gap-2 px-5 py-4 text-sm font-medium whitespace-nowrap transition-colors ${
                    activeTab === tab?.id
                      ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  <TabIcon className="w-4 h-4" />
                  <span>{tab?.label}</span>
                </button>
              );
            })}
          </div>

          <div className="p-6">
            {/* Personal Info Tab */}
            {activeTab === 'personal' && (
              <div className="space-y-5">
                <h2 className="text-lg font-semibold text-foreground">Personal Information</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">First Name *</label>
                    <input
                      type="text"
                      value={personalInfo?.firstName}
                      onChange={e => setPersonalInfo(p => ({ ...p, firstName: e?.target?.value }))}
                      className={inputClass('firstName')}
                      placeholder="Enter first name"
                    />
                    {fieldErrors?.firstName && <p className="text-xs text-destructive mt-1">{fieldErrors?.firstName}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Last Name</label>
                    <input
                      type="text"
                      value={personalInfo?.lastName}
                      onChange={e => setPersonalInfo(p => ({ ...p, lastName: e?.target?.value }))}
                      className={inputClass('lastName')}
                      placeholder="Enter last name"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Email Address</label>
                  <input
                    type="email"
                    value={personalInfo?.email}
                    disabled
                    className="w-full px-4 py-3 border border-border rounded-xl bg-muted text-muted-foreground text-sm cursor-not-allowed"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Email cannot be changed here. Contact support if needed.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Phone Number</label>
                  <input
                    type="tel"
                    value={personalInfo?.phone}
                    onChange={e => setPersonalInfo(p => ({ ...p, phone: e?.target?.value }))}
                    className={inputClass('phone')}
                    placeholder="+1 (555) 000-0000"
                  />
                  {fieldErrors?.phone && <p className="text-xs text-destructive mt-1">{fieldErrors?.phone}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Job Title</label>
                  <input
                    type="text"
                    value={personalInfo?.jobTitle}
                    onChange={e => setPersonalInfo(p => ({ ...p, jobTitle: e?.target?.value }))}
                    className={inputClass('jobTitle')}
                    placeholder="e.g. Marketing Director"
                  />
                </div>
                <div className="flex justify-end pt-2">
                  <button
                    onClick={handleSavePersonal}
                    disabled={saving}
                    className="flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 disabled:opacity-60 text-primary-foreground rounded-xl font-semibold text-sm transition-colors"
                  >
                    {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    <span>{saving ? 'Saving...' : 'Save Changes'}</span>
                  </button>
                </div>
              </div>
            )}

            {/* Business Info Tab */}
            {activeTab === 'business' && (
              <div className="space-y-5">
                <h2 className="text-lg font-semibold text-foreground">Business Information</h2>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Company Name</label>
                  <input
                    type="text"
                    value={businessInfo?.companyName}
                    onChange={e => setBusinessInfo(p => ({ ...p, companyName: e?.target?.value }))}
                    className={inputClass('companyName')}
                    placeholder="Your company name"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Business Type</label>
                    <select
                      value={businessInfo?.businessType}
                      onChange={e => setBusinessInfo(p => ({ ...p, businessType: e?.target?.value }))}
                      className={inputClass('businessType')}
                    >
                      <option value="">Select type</option>
                      {['B2B', 'B2C', 'Agency', 'Startup', 'Enterprise', 'Freelancer', 'Non-profit']?.map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Industry</label>
                    <select
                      value={businessInfo?.industry}
                      onChange={e => setBusinessInfo(p => ({ ...p, industry: e?.target?.value }))}
                      className={inputClass('industry')}
                    >
                      <option value="">Select industry</option>
                      {['Technology', 'SaaS', 'Marketing', 'Finance', 'Healthcare', 'Real Estate', 'E-commerce', 'Education', 'Consulting', 'Other']?.map(i => (
                        <option key={i} value={i}>{i}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Website</label>
                  <input
                    type="url"
                    value={businessInfo?.website}
                    onChange={e => setBusinessInfo(p => ({ ...p, website: e?.target?.value }))}
                    className={inputClass('website')}
                    placeholder="https://yourcompany.com"
                  />
                  {fieldErrors?.website && <p className="text-xs text-destructive mt-1">{fieldErrors?.website}</p>}
                </div>
                <div className="flex justify-end pt-2">
                  <button
                    onClick={handleSaveBusiness}
                    disabled={saving}
                    className="flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 disabled:opacity-60 text-primary-foreground rounded-xl font-semibold text-sm transition-colors"
                  >
                    {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    <span>{saving ? 'Saving...' : 'Save Changes'}</span>
                  </button>
                </div>
              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-foreground">Security Settings</h2>

                {/* Change Password */}
                <div className="bg-muted/30 border border-border rounded-xl p-5">
                  <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Lock className="w-4 h-4" /> Change Password
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">New Password</label>
                      <div className="relative">
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          value={passwordData?.newPassword}
                          onChange={e => setPasswordData(p => ({ ...p, newPassword: e?.target?.value }))}
                          className={inputClass('newPassword')}
                          placeholder="Enter new password (min 8 chars)"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(p => !p)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {fieldErrors?.newPassword && <p className="text-xs text-destructive mt-1">{fieldErrors?.newPassword}</p>}
                      {passwordData?.newPassword && (
                        <div className="mt-2">
                          <div className="flex gap-1">
                            {[1,2,3,4,5]?.map(i => (
                              <div key={i} className={`h-1 flex-1 rounded-full ${
                                i <= (passwordData?.newPassword?.length >= 12 ? 5 : passwordData?.newPassword?.length >= 8 ? 3 : 1)
                                  ? 'bg-green-500' : 'bg-gray-200'
                              }`} />
                            ))}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">Use 8+ chars with uppercase, lowercase & numbers</p>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">Confirm New Password</label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={passwordData?.confirmPassword}
                          onChange={e => setPasswordData(p => ({ ...p, confirmPassword: e?.target?.value }))}
                          className={inputClass('confirmPassword')}
                          placeholder="Confirm new password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(p => !p)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {fieldErrors?.confirmPassword && <p className="text-xs text-destructive mt-1">{fieldErrors?.confirmPassword}</p>}
                      {passwordData?.confirmPassword && passwordData?.newPassword === passwordData?.confirmPassword && (
                        <p className="text-xs text-green-600 mt-1 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Passwords match</p>
                      )}
                    </div>
                    <button
                      onClick={handleChangePassword}
                      disabled={changingPassword}
                      className="flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/90 disabled:opacity-60 text-primary-foreground rounded-xl font-semibold text-sm transition-colors"
                    >
                      {changingPassword ? <Loader className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                      <span>{changingPassword ? 'Updating...' : 'Update Password'}</span>
                    </button>
                  </div>
                </div>

                {/* Account Security Info */}
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-green-800">Account Secured</p>
                      <p className="text-sm text-green-700 mt-0.5">Your account is protected with email verification and secure authentication.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div className="space-y-5">
                <h2 className="text-lg font-semibold text-foreground">Notification Preferences</h2>
                <div className="space-y-3">
                  {[
                    { key: 'emailNotifications', label: 'Email Notifications', desc: 'Receive updates and alerts via email' },
                    { key: 'smsNotifications', label: 'SMS Notifications', desc: 'Get text messages for important alerts' },
                    { key: 'marketingEmails', label: 'Marketing Emails', desc: 'Product updates, tips, and promotions' },
                    { key: 'securityAlerts', label: 'Security Alerts', desc: 'Login attempts and security events' },
                    { key: 'weeklyReports', label: 'Weekly Reports', desc: 'Summary of your lead generation activity' },
                    { key: 'leadAlerts', label: 'Lead Alerts', desc: 'Notifications when new leads are captured' },
                  ]?.map(item => (
                    <div key={item?.key} className="flex items-center justify-between p-4 bg-muted/30 border border-border rounded-xl">
                      <div>
                        <p className="font-medium text-foreground text-sm">{item?.label}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{item?.desc}</p>
                      </div>
                      <button
                        onClick={() => setNotifications(p => ({ ...p, [item?.key]: !p?.[item?.key] }))}
                        className={`relative w-11 h-6 rounded-full transition-colors ${
                          notifications?.[item?.key] ? 'bg-primary' : 'bg-muted'
                        }`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                          notifications?.[item?.key] ? 'translate-x-5' : 'translate-x-0'
                        }`} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end pt-2">
                  <button
                    onClick={handleSaveNotifications}
                    disabled={saving}
                    className="flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 disabled:opacity-60 text-primary-foreground rounded-xl font-semibold text-sm transition-colors"
                  >
                    {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    <span>{saving ? 'Saving...' : 'Save Preferences'}</span>
                  </button>
                </div>
              </div>
            )}

            {/* Payment Methods Tab */}
            {activeTab === 'payment' && (
              <div className="space-y-5">
                <h2 className="text-lg font-semibold text-foreground">Payment Methods</h2>
                <div className="bg-muted/30 border border-border rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">Manage Payment Methods</p>
                      <p className="text-sm text-muted-foreground">Add or update your billing information</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-4 bg-card border border-border rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-7 bg-blue-600 rounded flex items-center justify-center">
                          <span className="text-white text-xs font-bold">VISA</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">•••• •••• •••• 4242</p>
                          <p className="text-xs text-muted-foreground">Expires 12/27</p>
                        </div>
                      </div>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">Default</span>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/subscription-management')}
                    className="mt-4 w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-border hover:border-primary hover:bg-primary/5 text-muted-foreground hover:text-primary rounded-xl font-medium text-sm transition-all"
                  >
                    <CreditCard className="w-4 h-4" />
                    <span>Manage Billing & Subscriptions</span>
                  </button>
                </div>
              </div>
            )}

            {/* Email Delivery Tracking Tab */}
            {activeTab === 'email-tracking' && (
              <EmailDeliveryTracker />
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default ProfileSettings;